import React, { useEffect, useState } from 'react';
import { UserAvatar } from '../components/ui/UserAvatar';
import { Card, CardHeader, Table, Btn, Badge, Modal, FormRow, FormField, Spinner } from '../components/ui/Card';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import toast from 'react-hot-toast';

// ─────────────────────────────────────────────────────────────
// Constantes rôles
// ─────────────────────────────────────────────────────────────
const ROLES = [
  'admin', 'direction', 'assistant_admin', 'rh',
  'responsable_stock', 'responsable_volontaires', 'responsable_bcc',
  'coordinateur', 'utilisateur',
];
const ROLES_FR = {
  admin:                  'Administrateur',
  super_admin:            'Super Admin',
  direction:              'Direction',
  assistant_admin:        'Assistant Admin',
  rh:                     'Ressources Humaines',
  responsable_stock:      'Resp. Stock',
  responsable_volontaires:'Resp. Volontaires',
  responsable_bcc:        'Resp. BCC',
  coordinateur:           'Coordinateur',
  utilisateur:            'Utilisateur',
};
const ROLE_BADGE = {
  admin: 'error', super_admin: 'error',
  direction: 'info', rh: 'info',
  assistant_admin: 'warning',
  responsable_stock: 'default', responsable_volontaires: 'default',
  responsable_bcc: 'default', coordinateur: 'default', utilisateur: 'default',
};

const defaultForm = { username: '', email: '', name: '', role: 'utilisateur', password: '', service: '', photo: '' };

// ─────────────────────────────────────────────────────────────
// Modale de confirmation
// ─────────────────────────────────────────────────────────────
function ConfirmModal({ open, title, message, onConfirm, onCancel, danger = true }) {
  if (!open) return null;
  return (
    <Modal open={open} onClose={onCancel} title={title}>
      <div style={{ padding: '20px 24px' }}>
        <p style={{ color: 'var(--gray-500)', marginBottom: 24, lineHeight: 1.6 }}>{message}</p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <Btn variant="secondary" onClick={onCancel}>Annuler</Btn>
          <Btn variant={danger ? 'danger' : 'gold'} onClick={onConfirm}>Confirmer</Btn>
        </div>
      </div>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────
// Page principale
// ─────────────────────────────────────────────────────────────
export default function UsersPage() {
  const { user: me } = useAuth();
  const [users,     setUsers]     = useState([]);
  const [services,  setServices]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing,   setEditing]   = useState(null);
  const [form,      setForm]      = useState(defaultForm);
  const [saving,    setSaving]    = useState(false);
  const [confirm,   setConfirm]   = useState(null); // { type, user }

  // ── Chargement ───────────────────────────────────────────
  const load = async (signal) => {
    setLoading(true);
    try {
      const [rUsers, rSvc] = await Promise.all([
        api.get('/users'),
        api.get('/services'),
      ]);
      if (!signal?.aborted) {
        setUsers(rUsers.data.data || []);
        setServices(rSvc.data.data || []);
      }
    } catch {
      if (!signal?.aborted) toast.error('Erreur lors du chargement');
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  };

  useEffect(() => {
    document.getElementById('page-title') &&
      (document.getElementById('page-title').textContent = 'Gestion des Utilisateurs');
    const ctrl = new AbortController();
    load(ctrl.signal);
    return () => ctrl.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Formulaire ───────────────────────────────────────────
  const openModal = (u = null) => {
    setEditing(u);
    setForm(u
      ? { username: u.username || '', email: u.email || '', name: u.name || '',
          role: u.role || 'utilisateur', password: '', service: u.service || '', photo: u.photo || '' }
      : defaultForm);
    setModalOpen(true);
  };

  const handleSave = async e => {
    e.preventDefault();
    if (!editing) return; // La création passe par Supabase directement (voir astuce ci-dessus)
    setSaving(true);
    try {
      await api.put(`/users/${editing.id}`, form);
      toast.success('Utilisateur mis à jour');
      setModalOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  // ── Actions ──────────────────────────────────────────────
  const handleToggleBlock = (u) => {
    setConfirm({
      type: 'block',
      user: u,
      title: u.locked ? '🔓 Déverrouiller le compte' : '🔒 Verrouiller le compte',
      message: u.locked
        ? `Déverrouiller le compte de ${u.name || u.username} ? L'utilisateur pourra se reconnecter.`
        : `Verrouiller le compte de ${u.name || u.username} ? L'utilisateur ne pourra plus se connecter.`,
      danger: !!u.locked === false,
    });
  };

  const handleDelete = (u) => {
    if (u.id === me?.id) { toast.error('Impossible de supprimer votre propre compte'); return; }
    setConfirm({
      type: 'delete',
      user: u,
      title: '🗑️ Supprimer l\'utilisateur',
      message: `Supprimer définitivement le compte de ${u.name || u.username} (${u.email}) ? Cette action est irréversible.`,
      danger: true,
    });
  };

  const executeConfirm = async () => {
    if (!confirm) return;
    const { type, user: u } = confirm;
    setConfirm(null);
    try {
      if (type === 'block') {
        await api.put(`/users/${u.id}/toggle-block`);
        toast.success(u.locked ? `${u.name || u.username} déverrouillé` : `${u.name || u.username} verrouillé`);
      } else if (type === 'delete') {
        await api.delete(`/users/${u.id}`);
        toast.success(`${u.name || u.username} supprimé`);
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Erreur');
    }
  };

  const f = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  // ── Colonnes du tableau ──────────────────────────────────
  const columns = [
    {
      key: 'name', header: 'Utilisateur',
      render: (v, r) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <UserAvatar user={r} size={36} />
          <div>
            <p style={{ fontWeight: 600, color: 'var(--navy)', margin: 0 }}>{v || r.username}</p>
            <p style={{ fontSize: '0.72rem', color: 'var(--gray-400)', margin: 0 }}>
              @{r.username} · {r.email}
            </p>
          </div>
        </div>
      ),
    },
    {
      key: 'role', header: 'Rôle',
      render: v => <Badge type={ROLE_BADGE[v] || 'default'}>{ROLES_FR[v] || v}</Badge>,
    },
    {
      key: 'service_name', header: 'Service',
      render: v => <span style={{ fontSize: '0.8rem', color: 'var(--gray-500)' }}>{v || '—'}</span>,
    },
    {
      key: 'active', header: 'Statut',
      render: (v, r) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Badge type={v ? 'success' : 'error'}>{v ? 'Actif' : 'Inactif'}</Badge>
          {r.locked && <Badge type="error">🔒 Verrouillé</Badge>}
        </div>
      ),
    },
    {
      key: 'last_login', header: 'Dernière connexion',
      render: v => (
        <span style={{ fontSize: '0.78rem', color: 'var(--gray-400)' }}>
          {v ? new Date(v).toLocaleString('fr-FR') : 'Jamais'}
        </span>
      ),
    },
    {
      key: 'actions', header: 'Actions',
      render: (_, row) => {
        const isSelf = row.id === me?.id;
        return (
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
            <Btn size="sm" variant="secondary" onClick={() => openModal(row)} title="Modifier">✏️</Btn>
            <Btn
              size="sm"
              variant={row.locked ? 'success' : 'warning'}
              onClick={() => handleToggleBlock(row)}
              title={row.locked ? 'Déverrouiller' : 'Verrouiller'}
            >
              {row.locked ? '🔓' : '🔒'}
            </Btn>
            {!isSelf && (
              <Btn size="sm" variant="danger" onClick={() => handleDelete(row)} title="Supprimer">🗑️</Btn>
            )}
          </div>
        );
      },
    },
  ];

  // ── Statistiques rapides ─────────────────────────────────
  const stats = {
    total:   users.length,
    actifs:  users.filter(u => u.active && !u.locked).length,
    admins:  users.filter(u => u.role === 'admin' || u.role === 'super_admin').length,
    locked:  users.filter(u => u.locked).length,
  };

  return (
    <div className="animate-fade">
      {/* Stats rapides */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px,1fr))', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Total', value: stats.total, color: '#0f2044' },
          { label: 'Actifs', value: stats.actifs, color: '#16a34a' },
          { label: 'Admins', value: stats.admins, color: '#c9a84c' },
          { label: 'Verrouillés', value: stats.locked, color: '#dc2626' },
        ].map(s => (
          <div key={s.label} style={{
            background: 'white', borderRadius: 10, padding: '14px 18px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)', borderLeft: `4px solid ${s.color}`,
          }}>
            <p style={{ fontSize: '1.6rem', fontWeight: 700, color: s.color, margin: 0 }}>{s.value}</p>
            <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: 0 }}>{s.label}</p>
          </div>
        ))}
      </div>

      {/* ── Astuce création utilisateur ─────────────────────── */}
      <div style={{
        background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10,
        padding: '14px 18px', marginBottom: 16, fontSize: '0.82rem', color: '#1e40af',
        lineHeight: 1.7,
      }}>
        <strong>💡 Comment ajouter un nouvel utilisateur ?</strong>
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span><strong>Étape 1</strong> — Allez sur <strong>supabase.com</strong> → votre projet → <strong>Authentication → Users → Add user</strong></span>
          <span><strong>Étape 2</strong> — Saisissez l'email et le mot de passe, cochez <em>"Auto Confirm User"</em> puis cliquez <strong>Create User</strong></span>
          <span><strong>Étape 3</strong> — Allez dans <strong>Table Editor → users</strong> → cliquez <strong>Insert row</strong></span>
          <span style={{ paddingLeft: 16, color: '#2563eb' }}>
            • <code>id</code> = l'UUID copié depuis Authentication → Users (colonne User UID)<br/>
            • <code>email</code> = même email qu'à l'étape 1<br/>
            • <code>username</code> = identifiant court (ex: <em>jean.dupont</em>)<br/>
            • <code>name</code> = nom complet affiché<br/>
            • <code>role</code> = admin / direction / rh / responsable_stock / responsable_volontaires / responsable_bcc / coordinateur / utilisateur<br/>
            • <code>active</code> = true &nbsp;|&nbsp; <code>locked</code> = false &nbsp;|&nbsp; <code>failed_attempts</code> = 0
          </span>
          <span><strong>Étape 4</strong> — Rechargez cette page — le nouvel utilisateur apparaît dans la liste.</span>
        </div>
      </div>

      <Card>
        <CardHeader
          title="Gestion des Utilisateurs"
          subtitle={`${users.length} utilisateur(s) enregistré(s)`}
        />
        {loading
          ? <Spinner />
          : <Table columns={columns} data={users} emptyMessage="Aucun utilisateur" />
        }
      </Card>

      {/* ── Modale Édition uniquement ────────────────────────── */}
      <Modal
        open={modalOpen && !!editing}
        onClose={() => setModalOpen(false)}
        title={`✏️ Modifier — ${editing?.username}`}
      >
        <form onSubmit={handleSave} style={{ padding: 24 }}>
          <FormRow>
            <FormField label="Nom complet">
              <input value={form.name} onChange={e => f('name', e.target.value)} />
            </FormField>
            <FormField label="Identifiant">
              <input value={form.username} disabled style={{ opacity: 0.5 }} />
            </FormField>
          </FormRow>
          <FormRow cols={1}>
            <FormField label="Email">
              <input
                type="email"
                value={form.email}
                onChange={e => f('email', e.target.value)}
                required
              />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField label="Rôle">
              <select value={form.role} onChange={e => f('role', e.target.value)}>
                {ROLES.map(r => (
                  <option key={r} value={r}>{ROLES_FR[r] || r}</option>
                ))}
              </select>
            </FormField>
            <FormField label="Service">
              <select value={form.service} onChange={e => f('service', e.target.value)}>
                <option value="">— Aucun service —</option>
                {services.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </FormField>
          </FormRow>
          <FormRow cols={1}>
            <FormField label="Nouveau mot de passe (laisser vide pour ne pas changer)">
              <input
                type="password"
                value={form.password}
                onChange={e => f('password', e.target.value)}
                placeholder="Laisser vide pour conserver l'actuel"
              />
            </FormField>
          </FormRow>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <Btn type="button" variant="secondary" onClick={() => setModalOpen(false)}>Annuler</Btn>
            <Btn type="submit" variant="gold" disabled={saving}>
              {saving ? 'Enregistrement...' : '💾 Mettre à jour'}
            </Btn>
          </div>
        </form>
      </Modal>

      {/* ── Modale de confirmation ───────────────────────── */}
      <ConfirmModal
        open={!!confirm}
        title={confirm?.title || ''}
        message={confirm?.message || ''}
        danger={confirm?.danger !== false}
        onConfirm={executeConfirm}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}
